import React, { createContext, useContext, useState, useCallback } from 'react';

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be inside WorkspaceProvider');
  return ctx;
};

export const WORKSPACE_PAGES = [
  { id: 'dashboard', label: 'Dashboard', path: '/', icon: 'grid', category: 'gestion' },
  { id: 'inbox', label: 'Inbox', path: '/inbox', icon: 'inbox', category: 'gestion' },
  { id: 'notifications', label: 'Notificaciones', path: '/notifications', icon: 'bell', category: 'comunicacion' },
  { id: 'pipeline', label: 'Pipeline', path: '/bubbles', icon: 'circle-dot', category: 'gestion' },
  { id: 'deals', label: 'Gestión Noah', path: '/deals', icon: 'sparkles', category: 'gestion' },
  { id: 'customers', label: 'Clientes', path: '/customers', icon: 'users', category: 'gestion' },
  { id: 'tickets', label: 'Tickets', path: '/tickets', icon: 'ticket', category: 'gestion' },
  { id: 'automations', label: 'Automatizaciones', path: '/automations', icon: 'zap', category: 'marketing' },
  { id: 'reports', label: 'Reportes', path: '/reports', icon: 'bar-chart', category: 'analisis' },
  { id: 'campaigns', label: 'Campañas', path: '/campaigns', icon: 'megaphone', category: 'marketing' },
  { id: 'products', label: 'Productos', path: '/products', icon: 'package', category: 'marketing' },
  { id: 'analytics', label: 'Analytics', path: '/analytics', icon: 'bar-chart', category: 'analisis' },
  { id: 'escrow', label: 'Pagos Seguros', path: '/escrow', icon: 'shield', category: 'finanzas' },
  { id: 'invoices', label: 'Facturacion', path: '/invoices', icon: 'file-text', category: 'finanzas' },
  { id: 'payment-methods', label: 'Metodos de Pago', path: '/payment-methods', icon: 'credit-card', category: 'finanzas' },
  { id: 'bruno', label: 'Bruno App', path: '/bruno', icon: 'globe', category: 'comunicacion' },
  { id: 'connections', label: 'Conexiones', path: '/connections', icon: 'link', category: 'comunicacion' },
  { id: 'social', label: 'Redes Sociales', path: '/social', icon: 'globe', category: 'comunicacion' },
  { id: 'admin-users', label: 'Equipo', path: '/admin/users', icon: 'user-cog', category: 'admin' },
  { id: 'plans', label: 'Planes', path: '/plans', icon: 'crown', category: 'admin' },
  { id: 'profile', label: 'Mi Perfil', path: '/profile', icon: 'user', category: 'admin' },
  { id: 'settings', label: 'Configuracion', path: '/settings', icon: 'settings', category: 'admin' },
];

const PANELS = ['tabs', 'noah', 'deal', 'notifications', 'chats'];

export const WorkspaceProvider = ({ children }) => {
  // Lazy init from URL: if user lands on a workspace path, start there
  const initialPage = (() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const page = WORKSPACE_PAGES.find(p => p.path === path);
    return page || WORKSPACE_PAGES[0];
  })();
  const [openTabs, setOpenTabs] = useState([
    { ...initialPage, minimized: false, pinned: false }
  ]);
  const [activeTabId, setActiveTabId] = useState(initialPage.id);
  const [activePanel, setActivePanel] = useState(null);
  // Minimized chat bubbles - persist independently
  const [chatBubbles, setChatBubbles] = useState([]);

  const togglePanel = useCallback((panel) => {
    setActivePanel(prev => prev === panel ? null : panel);
  }, []);

  const closeAllPanels = useCallback(() => setActivePanel(null), []);

  const openWorkspace = useCallback((pageId) => {
    const page = WORKSPACE_PAGES.find(p => p.id === pageId);
    if (!page) return;
    setOpenTabs(prev => {
      const existing = prev.find(t => t.id === pageId);
      if (existing) {
        return prev.map(t => t.id === pageId ? { ...t, minimized: false } : t);
      }
      return [...prev, { ...page, minimized: false, pinned: false }];
    });
    setActiveTabId(pageId);
    setActivePanel(null);
  }, []);

  const closeTab = useCallback((tabId) => {
    setOpenTabs(prev => {
      const filtered = prev.filter(t => t.id !== tabId);
      if (filtered.length === 0) {
        return [{ id: 'dashboard', label: 'Dashboard', path: '/', icon: 'grid', minimized: false, pinned: false }];
      }
      return filtered;
    });
    setActiveTabId(prev => {
      if (prev !== tabId) return prev;
      const remaining = openTabs.filter(t => t.id !== tabId && !t.minimized);
      return remaining.length > 0 ? remaining[remaining.length - 1].id : 'dashboard';
    });
  }, [openTabs]);

  const minimizeTab = useCallback((tabId) => {
    setOpenTabs(prev => prev.map(t => t.id === tabId ? { ...t, minimized: true, pinned: true } : t));
    setActiveTabId(prev => {
      if (prev !== tabId) return prev;
      const visible = openTabs.filter(t => !t.minimized && t.id !== tabId);
      return visible.length > 0 ? visible[visible.length - 1].id : 'dashboard';
    });
  }, [openTabs]);

  const restoreTab = useCallback((tabId) => {
    setOpenTabs(prev => prev.map(t => t.id === tabId ? { ...t, minimized: false } : t));
    setActiveTabId(tabId);
    setActivePanel(null);
  }, []);

  // Chat bubble management
  const addChatBubble = useCallback((chat) => {
    setChatBubbles(prev => {
      if (prev.find(b => b.id === chat.id)) return prev;
      return [...prev, { id: chat.id, customer_name: chat.customer?.name || 'Cliente', customer_id: chat.customer_id, campaign_id: chat.campaign_id, last_message: chat.last_message }];
    });
  }, []);

  const removeChatBubble = useCallback((chatId) => {
    setChatBubbles(prev => prev.filter(b => b.id !== chatId));
  }, []);

  const activeTab = openTabs.find(t => t.id === activeTabId && !t.minimized);
  const visibleTabs = openTabs.filter(t => !t.minimized);
  const pinnedTabs = openTabs.filter(t => t.pinned);

  return (
    <WorkspaceContext.Provider value={{
      openTabs, activeTabId, activeTab, visibleTabs, pinnedTabs,
      openWorkspace, closeTab, minimizeTab, restoreTab, setActiveTabId,
      activePanel, togglePanel, closeAllPanels,
      chatBubbles, addChatBubble, removeChatBubble,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
